import React, { useState } from 'react';
import { useAppUser } from '../../../hooks/useAppUser';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useApi } from '../../../hooks/useApi';

function CopyableField({ label, value, mono }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{
        color: "text.secondary"
      }}>
        {label}
        {copied && (
          <Chip label="Copied!" size="small" color="success" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
        )}
      </Typography>
      <TextField
        fullWidth
        size="small"
        value={value || 'Not available'}
        slotProps={{
          input: {
            readOnly: true,
            sx: mono ? { fontFamily: 'monospace', fontSize: '0.85rem' } : {},
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </Box>
  );
}

/**
 * Displays all 4 newly generated keys after regeneration (one-time view)
 */
function NewKeysDisplay({ data, onDismiss }) {
  const [copied, setCopied] = useState({});

  const handleCopy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied((prev) => ({ ...prev, [key]: false })), 2000);
    } catch { /* ignore */ }
  };

  const keys = [
    { label: 'Live Publishable Key', value: data.live.publishable, key: 'pk_live' },
    { label: 'Live Secret Key', value: data.live.secret, key: 'sk_live', isSecret: true },
    { label: 'Test Publishable Key', value: data.test.publishable, key: 'pk_test' },
    { label: 'Test Secret Key', value: data.test.secret, key: 'sk_test', isSecret: true },
  ];

  return (
    <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'warning.main' }}>
      <Typography variant="h6" gutterBottom sx={{
        color: "warning.main"
      }}>
        New Keys Generated — Copy Your Secrets Now
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Your <strong>secret keys</strong> are shown below for the <strong>first and only time</strong>.
        Copy them to a secure location now. They cannot be retrieved later.
      </Alert>
      {keys.map(({ label, value, key, isSecret }) => (
        <Box key={key} sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            {label}
            {isSecret && <Chip label="Copy now!" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                wordBreak: 'break-all',
                flex: 1,
                bgcolor: isSecret ? 'rgba(255,167,38,0.08)' : 'transparent',
                p: isSecret ? 0.5 : 0,
                borderRadius: 1,
              }}
            >
              {value}
            </Typography>
            <IconButton size="small" onClick={() => handleCopy(key, value)}>
              {copied[key] ? (
                <Chip label="Copied!" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
      ))}
      <Button variant="outlined" size="small" onClick={onDismiss} sx={{ mt: 2 }}>
        I have copied my secrets
      </Button>
    </Paper>
  );
}

function ApiKeyManagement() {
  const { user } = useAppUser();
  const api = useApi();
  const queryClient = useQueryClient();
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [regeneratedKeys, setRegeneratedKeys] = useState(null);

  const userId = user?.id || user?._id || user?.email;

  const { data: apiKeyData, isLoading } = useQuery({
    queryKey: ['api-keys', userId],
    queryFn: () => api.get('/api/api-keys'),
    enabled: !!userId,
  });

  const regenerateMutation = useMutation({
    mutationFn: () => api.post('/api/api-keys/regenerate'),
    onSuccess: (data) => {
      setRegeneratedKeys(data);
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const live = apiKeyData?.live;
  const test = apiKeyData?.test;
  const legacyApiKey = apiKeyData?.legacyApiKey;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">API Keys</Typography>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<AutorenewIcon />}
          onClick={() => setConfirmDialog(true)}
          disabled={regenerateMutation.isPending}
        >
          {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate Keys'}
        </Button>
      </Box>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4
        }}>
        Your SDK credentials for authenticating with the Hiyve platform.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Publishable keys</strong> are safe to include in client-side code.{' '}
        <strong>Secret keys</strong> must only be used server-side and are shown once at generation time.
        Use <strong>Test</strong> keys for development and <strong>Live</strong> keys for production.
      </Alert>
      {/* Show newly regenerated keys (one-time display) */}
      {regeneratedKeys && (
        <NewKeysDisplay data={regeneratedKeys} onDismiss={() => setRegeneratedKeys(null)} />
      )}
      {regenerateMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {regenerateMutation.error?.message || 'Failed to regenerate keys'}
        </Alert>
      )}
      {/* Live Keys */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Live Keys</Typography>
          <Chip label="Production" size="small" color="success" sx={{ ml: 1.5 }} />
        </Box>
        <CopyableField label="Publishable Key" value={live?.publishable} mono />
        <Box sx={{ mb: 0 }}>
          <Typography variant="subtitle2" gutterBottom sx={{
            color: "text.secondary"
          }}>
            Secret Key
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={live?.secretPrefix || 'Hidden — use Regenerate Keys to get a new secret'}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.disabled' } } }}
          />
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            Secret keys are only shown once at generation time. Use Regenerate Keys to create a new one.
          </Typography>
        </Box>
      </Paper>
      {/* Test Keys */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Test Keys</Typography>
          <Chip label="Development" size="small" color="warning" sx={{ ml: 1.5 }} />
        </Box>
        {test?.publishable ? (
          <>
            <CopyableField label="Publishable Key" value={test.publishable} mono />
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{
                color: "text.secondary"
              }}>
                Secret Key
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={test?.secretPrefix || 'Hidden — use Regenerate Keys to get a new secret'}
                slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.disabled' } } }}
              />
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                Secret keys are only shown once at generation time. Use Regenerate Keys to create a new one.
              </Typography>
            </Box>
          </>
        ) : (
          <Typography sx={{
            color: "text.secondary"
          }}>
            No test keys available. Use Regenerate Keys to create them.
          </Typography>
        )}
      </Paper>
      {/* Legacy Key */}
      {legacyApiKey && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Legacy API Key</Typography>
            <Chip label="Deprecated" size="small" color="default" sx={{ ml: 1.5 }} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            This key is from the previous authentication system. Migrate to identity keys above for full functionality.
          </Typography>
          <CopyableField label="API Key" value={legacyApiKey} mono />
        </Paper>
      )}
      {/* Confirm Regenerate Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Regenerate All API Keys?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will <strong>revoke all existing keys</strong> and generate new ones.
            Any applications using your current keys will stop working immediately.
            This action cannot be undone.
          </DialogContentText>
          {legacyApiKey && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Your legacy API key (<strong>{legacyApiKey}</strong>) will also be invalidated.
              All services, SDKs, and integrations using this key will stop working until
              you replace it with the new <code>pk_</code> and <code>sk_</code> keys.
            </Alert>
          )}
          <DialogContentText sx={{ mt: 1 }}>
            Your new secret keys will be displayed <strong>once only</strong> — make sure you are ready to copy them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={() => { setConfirmDialog(false); regenerateMutation.mutate(); }}
            variant="contained"
            color="warning"
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApiKeyManagement;
